import type { DeliveryAddressParams } from '@iheartjane/data-access';
import { Storage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

import type { Address } from '@jane/shared/types';

// Not the slice's `deliveryAddress` key: flag-on the slice cart is empty, so
// `clearCart()` wipes that key on boot. Cleared with the cart uuid instead.
const DELIVERY_ADDRESS_KEY = 'cart-delivery-address';

const listeners = new Set<() => void>();

let cachedAddress: Address | null = null;
let cachedSerialized: string | null = null;

// `Storage.get` returns a new object each call; reuse it so
// `useSyncExternalStore` doesn't re-render forever.
export const getLocalDeliveryAddress = (): Address | null => {
  const stored = Storage.get<Address | null>(DELIVERY_ADDRESS_KEY) ?? null;
  const serialized = JSON.stringify(stored);

  if (serialized !== cachedSerialized) {
    cachedSerialized = serialized;
    cachedAddress = stored;
  }

  return cachedAddress;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const updateLocalDeliveryAddress = (address: Address | null) => {
  if (address) {
    Storage.set(DELIVERY_ADDRESS_KEY, address);
  } else {
    Storage.remove(DELIVERY_ADDRESS_KEY);
  }

  listeners.forEach((listener) => listener());
};

/** Hooks can't be conditional, so `enabled` skips the storage read instead. */
export const useLocalDeliveryAddress = (enabled = true) =>
  useSyncExternalStore(
    subscribe,
    () => (enabled ? getLocalDeliveryAddress() : null),
    () => null
  );

/**
 * Part of the query key, so fields the API doesn't use must not change it.
 * Sent in every mode: the API only applies it to delivery carts.
 */
export const toDeliveryAddressParams = (
  address: Address | null | undefined
): DeliveryAddressParams | undefined => {
  if (!address) return undefined;

  const params: DeliveryAddressParams = {
    ...(address.city != null && { city: address.city }),
    ...(address.country_code != null && { country_code: address.country_code }),
    ...(address.lat != null && { latitude: address.lat }),
    ...(address.lng != null && { longitude: address.lng }),
    ...(address.state_code != null && { state_code: address.state_code }),
    ...(address.street != null && { street: address.street }),
    ...(address.street2 != null && { street2: address.street2 }),
    ...(address.zipcode != null && { zipcode: address.zipcode }),
  };

  return Object.keys(params).length ? params : undefined;
};
